// R-SIS-3d — Term context (§3.1.5 navbar term switcher).
//
// Loads the tenant's terms + the active term once the user is authenticated,
// and tracks the user's SELECTED term (persisted). Registration (§6.5) always
// operates on the ACTIVE term server-side; the selected term is what
// term-aware *views* (transcript R-SIS-4, weekly schedule, …) read. The
// navbar switcher in shared.tsx is the UI; this is the state.

import React from "react";

import { useAuth } from "./auth/AuthContext";
import { termsApi } from "./api/endpoints.js";

export interface TermLite {
  id: string;
  title: string;
  year: number;
  semester: number;
  status: "UPCOMING" | "ACTIVE" | "ENDED";
}

interface TermContextValue {
  terms: TermLite[];
  activeTerm: TermLite | null;
  selectedTermId: string | null;
  setSelectedTermId: (id: string) => void;
  loading: boolean;
}

const TermContext = React.createContext<TermContextValue>({
  terms: [],
  activeTerm: null,
  selectedTermId: null,
  setSelectedTermId: () => {},
  loading: false,
});

export const useTerm = (): TermContextValue => React.useContext(TermContext);

const STORAGE_KEY = "digiu_selected_term";

export const TermProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [terms, setTerms] = React.useState<TermLite[]>([]);
  const [activeTerm, setActiveTerm] = React.useState<TermLite | null>(null);
  const [selectedTermId, setSel] = React.useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setTerms([]);
      setActiveTerm(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([termsApi.list(), termsApi.getActive()])
      .then(([list, active]: [TermLite[], TermLite | null]) => {
        if (cancelled) return;
        const all = list ?? [];
        setTerms(all);
        setActiveTerm(active ?? null);
        // Keep a still-valid persisted selection, else default to the active term.
        setSel((prev) => {
          if (prev && all.some((t) => t.id === prev)) return prev;
          return active?.id ?? null;
        });
      })
      .catch(() => {
        /* navbar switcher is non-critical; ignore load errors */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const setSelectedTermId = React.useCallback((id: string) => {
    setSel(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* ignore */
    }
  }, []);

  const value = React.useMemo<TermContextValue>(
    () => ({ terms, activeTerm, selectedTermId, setSelectedTermId, loading }),
    [terms, activeTerm, selectedTermId, setSelectedTermId, loading],
  );

  return <TermContext.Provider value={value}>{children}</TermContext.Provider>;
};
